import { Box, Checkbox, Radio, Typography } from "@material-ui/core";
import React, { useState } from "react";
import { DesktopPopupModal, ActionButton, ActionLink } from "halifax";

import "./styles.scss";
import {
  SurveyState,
  SurveyStateCheckbox,
  SurveyStateObject,
  SurveyStateRadio,
} from "./types";
import clsx from "clsx";
import { surveyStates } from "./config";
import { trackEvent } from "../../api/v0/analytics/trackEvent";
import { ACTIVITY_EXIT_ANSWERED_QUESTION } from "redmond";

export interface IActivitiesSurveyProps {
  isOpen: boolean;
  setOpen: (open: boolean) => void;
  isMobile?: boolean;
  trackingProperties?: any;
  encryptedProperties?: string[];
}

export const ActivitiesSurvey = (props: IActivitiesSurveyProps) => {
  const { isOpen, setOpen, isMobile, trackingProperties, encryptedProperties } = props;

  const [surveyState, setSurveyState] = useState<SurveyStateObject>(
    surveyStates[0]
  );

  const [randomNumber] = useState(Math.random());

  const shuffle = (array: any[], seed: number) => {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(seed * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }

    return array;
  };

  const ModalRadioOptions = (props: { surveyState: SurveyStateRadio }) => {
    const { surveyState } = props;
    let options = surveyState.options;

    if (surveyState.randomizeOptions) {
      options = shuffle([...surveyState.options], randomNumber);
    }

    const [selectedOption, setSelectedOption] = useState<{
      title: string;
      next: SurveyState;
    }>();
    return (
      <Box className="activities-survey-options-container">
        <Box className="activities-survey-options">
          {options.map((option) => (
            <Box
              key={option.title}
              className="activities-survey-radio-option"
              onClick={() => setSelectedOption(option)}
            >
              <Radio
                className={"activities-survey-radio"}
                checked={option === selectedOption}
                onChange={() => setSelectedOption(option)}
              />
              <Typography className={"activities-option-label"}>
                {option.title}
              </Typography>
            </Box>
          ))}
        </Box>
        <Box className={clsx("activities-survey-button-container")}>
          {surveyState.showBack ? (
            <ActionLink
              className={clsx("activities-survey-back-link")}
              onClick={() => {
                const prevState = surveyStates.find((x) => {
                  return x.state === surveyState.prev;
                });
                if (prevState) {
                  setSelectedOption(undefined);
                  setSurveyState(prevState);
                }
              }}
              content={"Back"}
            />
          ) : null}
          <ActionButton
            onClick={() => {
              if (selectedOption && surveyStates && surveyStates.length > 0) {
                const nextState = surveyStates.find((x) => {
                  return x.state === selectedOption.next;
                });
                if (nextState) {
                  trackEvent({
                    eventName: ACTIVITY_EXIT_ANSWERED_QUESTION,
                    properties: {
                      ...trackingProperties,
                      question: surveyState.question,
                      answer: selectedOption.title,
                    },
                    encryptedProperties,
                  });
                  setSurveyState(nextState);
                }
              }
            }}
            className={clsx("activities-survey-next-button")}
            message={"Next"}
          />
        </Box>
      </Box>
    );
  };

  const ModalCheckboxOptions = (props: {
    surveyState: SurveyStateCheckbox;
  }) => {
    const { surveyState } = props;
    let options = surveyState.options;

    if (surveyState.randomizeOptions) {
      options = shuffle([...surveyState.options], randomNumber);
    }
    const [selectedOptions, setSelectedOptions] = useState<string[]>([]);

    const isChecked = (option: string): boolean => {
      return selectedOptions.some((x) => x === option);
    };

    const handleChange = (option: string) => {
      if (isChecked(option)) {
        const selectedAfterRemove = selectedOptions.filter(
          (x: string) => x !== option
        );
        setSelectedOptions(selectedAfterRemove);
      } else {
        const newSelected = [...selectedOptions, option] as string[];
        setSelectedOptions(newSelected);
      }
    };

    return (
      <Box className="activities-survey-options-container">
        <Box className="activities-survey-options">
          {options.map((option) => (
            <Box
              key={option}
              className="activities-survey-checkbox-option"
              onClick={() => handleChange(option)}
            >
              <Checkbox
                className={"activities-survey-checkbox"}
                checked={isChecked(option)}
                onChange={() => handleChange(option)}
              />
              <Typography className={"activities-option-label"}>
                {option}
              </Typography>
            </Box>
          ))}
        </Box>
        <Box className={clsx("activities-survey-button-container")}>
          {surveyState.showBack && (
            <ActionLink
              className={clsx("activities-survey-back-link")}
              onClick={() => {
                const prevState = surveyStates.find((x) => {
                  return x.state === surveyState.prev;
                });
                if (prevState) {
                  setSelectedOptions([]);
                  setSurveyState(prevState);
                }
              }}
              content={"Back"}
            />
          )}
          <ActionButton
            onClick={() => {
              if (selectedOptions && surveyState.next) {
                const nextState = surveyStates.find((x) => {
                  return x.state === surveyState.next;
                });
                if (nextState) {
                  selectedOptions.forEach((option) => {
                    trackEvent({
                      eventName: ACTIVITY_EXIT_ANSWERED_QUESTION,
                      properties: {
                        ...trackingProperties,
                        question: surveyState.question,
                        answer: option,
                      },
                      encryptedProperties,
                    });
                  });
                  setSurveyState(nextState);
                }
              }
            }}
            className={clsx("activities-survey-next-button")}
            message={"Next"}
          />
        </Box>
      </Box>
    );
  };

  const ModalContent = React.useCallback(() => {
    return (
      <Box className="activities-survey-content">
        <Typography variant="h4" className="activities-survey-title">
          {surveyState.title ?? "Help us improve activities"}
        </Typography>

        <Typography className="activities-survey-subtitle">
          {surveyState.question}
        </Typography>
        {surveyState.optionType == "radio" && (
          <ModalRadioOptions
            surveyState={surveyState as any as SurveyStateRadio}
          />
        )}
        {surveyState.optionType == "checkbox" && (
          <ModalCheckboxOptions
            surveyState={surveyState as any as SurveyStateCheckbox}
          />
        )}
        {surveyState.optionType === "none" && (
          <ActionButton
            onClick={() => setOpen(false)}
            className={clsx("activities-survey-done-button")}
            message={"Done"}
          />
        )}
      </Box>
    );
  }, [surveyState]);

  return (
    <DesktopPopupModal
      open={isOpen}
      className={clsx("activities-survey-dialog", { mobile: isMobile })}
      onClose={() => setOpen(false)}
      invisibleBackdrop={false}
    >
      <ModalContent />
    </DesktopPopupModal>
  );
};
